/**
 * The details panel: the selected song's cover, what Nova knows about it, and
 * how sure it is. Read-only here; "Edit tags" opens the tag editor.
 */

import { useEffect, useState } from "react";
import { fetchTrackDetail, type TrackDetail } from "../api";
import { announce, editableFor, knownValue, parseValue, showValue, writeValues, type EditableColumn } from "../core/edits";
import { toast } from "../shell/Toast";
import { useApp } from "../core/app";
import { changeCover, removeCover } from "../core/commands";
import { player } from "../core/player";
import { musicalName } from "../format/keys";
import { Artwork } from "../shell/Artwork";
import { Icon } from "../shell/Icons";
import { MoreButton } from "../shell/PopupMenu";
import { clockTime } from "../shell/Waveform";
import type { Track } from "../types";
import { trackMenu } from "./trackMenu";
import { useMay } from "../core/session";

function confidenceWord(c: number | null | undefined): { word: string; cls: string } {
  if (c === null || c === undefined) return { word: "measured", cls: "hi" };
  if (c >= 0.75) return { word: "high", cls: "hi" };
  if (c >= 0.45) return { word: "medium", cls: "md" };
  return { word: "low", cls: "lo" };
}

/** The fields the details panel shows, each editable in place. */
const DETAIL_FIELDS = ["title", "artist", "album", "album_artist", "year", "genre", "subgenre", "comment"];

/** What a field holds: from the file when it has been read, else from the list. */
function detailValue(track: Track, col: EditableColumn, detail: TrackDetail | null): unknown {
  if (detail && col.kind === "tag") {
    const v = col.novaTag ? detail.nova?.[col.field] : detail.fields?.[col.field];
    if (v !== undefined) return v || null;
  }
  const known = knownValue(track, col);
  return known === undefined ? null : known;
}

/** One value: shown as text, a click turns it into a box, Enter writes it (with Undo). */
function InlineField({ track, col, value, parts }: { track: Track; col: EditableColumn; value: unknown; parts?: string[] }) {
  const app = useApp();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [shown, setShown] = useState<unknown>(value);
  useEffect(() => setShown(value), [value, track.id]);
  const start = () => {
    if (app.offline) return;
    setText(showValue(shown));
    setEditing(true);
  };
  const commit = async () => {
    const parsed = parseValue(col, text);
    if (parsed.error) return toast(parsed.error, "bad");
    if (showValue(parsed.value) === showValue(shown)) return setEditing(false);
    setSaving(true);
    const outcome = await writeValues([track.id], [{ col, value: parsed.value }]);
    setSaving(false);
    if (!outcome.error) {
      setShown(parsed.value);
      setEditing(false);
    }
    announce(outcome, `${col.label} saved${col.kind === "tag" ? " to the file" : ""}.`, () => void app.library.refresh());
  };
  return (
    <>
      <dt>{col.label}</dt>
      <dd>
        {editing ? (
          <input
            className="nv-input nv-inline-input"
            value={text}
            autoFocus
            disabled={saving}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => void commit()}
            onKeyDown={(e) => {
              if (e.key === "Enter") void commit();
              if (e.key === "Escape") { e.stopPropagation(); setEditing(false); }
            }}
          />
        ) : (
          <button className="nv-inline-value" onClick={start} title={app.offline ? undefined : `Click to edit ${col.label.toLowerCase()}`}>
            {/* Split as the Library settings say (the engine's parts), until edited here. */}
            {parts && parts.length > 1 && showValue(shown) === showValue(value)
              ? <span className="chips">{parts.map((p) => <span key={p} className="chip">{p}</span>)}</span>
              : showValue(shown) || <span className="nv-dim">—</span>}
          </button>
        )}
      </dd>
    </>
  );
}

export function TrackPanel({ track, onClose }: { track: Track | null; onClose?: () => void }) {
  const app = useApp();
  const mayDo = useMay();
  const [tab, setTab] = useState<"details" | "analysis" | "file">("details");
  const [detail, setDetail] = useState<TrackDetail | null>(null);

  useEffect(() => {
    setDetail(null);
    if (!track || tab !== "file" || app.offline) return;
    let live = true;
    void fetchTrackDetail(track.id).then((d) => live && setDetail(d));
    return () => {
      live = false;
    };
  }, [track, tab, app.offline]);

  if (!track) {
    return (
      <aside className="nv-insp empty" aria-label="Details">
        <Icon name="info" size={22} />
        <span>Select a song to see its details.</span>
      </aside>
    );
  }
  const key = track.musicalKey && track.musicalKey !== "ambiguous" ? track.musicalKey : null;
  const keyShown = key ? (app.notation === "musical" ? track.musicalKeyName ?? musicalName(key) : key) : "—";
  const bpmConf = confidenceWord(track.bpmConfidence);
  const keyConf = confidenceWord(track.keyConfidence);

  return (
    <aside className="nv-insp" aria-label={`Details for ${track.title}`}>
      <div className="nv-insp-scroll">
        <button className="nv-cover" onDoubleClick={() => player.playTracks([track])} title="Double-click to play">
          <Artwork trackId={track.id} seed={track.album || track.artist || track.title} rounded={10} alt={`Cover of ${track.album ?? track.title}`} />
        </button>
        <div className="nv-insp-title">
          <h2 title={track.title}>{track.title}</h2>
          <div className="nv-sub">
            {[track.artist, track.album, track.year].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="nv-stats">
          <div><small>BPM</small><b>{track.bpm != null ? Math.round(track.bpm) : "—"}</b></div>
          <div><small>Key</small><b title={key ? `${key} · ${track.musicalKeyName ?? musicalName(key)}` : undefined}>{keyShown}</b></div>
          <div><small>LUFS</small><b>{track.lufs != null ? track.lufs.toFixed(1).replace("-", "−") : "—"}</b></div>
        </div>
        <div className="nv-tabs" role="tablist">
          {(["details", "analysis", "file"] as const).map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
              {t === "details" ? "Details" : t === "analysis" ? "Analysis" : "File"}
            </button>
          ))}
        </div>
        {tab === "details" && (
          <dl className="nv-kv nv-kv-edit">
            {DETAIL_FIELDS.map((id) => {
              const col = editableFor(id, app.library.columns)!;
              const parts = id === "comment" ? track.commentParts : id === "genre" ? track.genres : id === "subgenre" ? track.subGenres : undefined;
              return <InlineField key={id} track={track} col={col} value={detailValue(track, col, detail)} parts={parts} />;
            })}
            <dt>Energy</dt>
            <dd>
              {track.energyLevel != null ? (
                <span className="energy-bars" aria-label={`Energy ${track.energyLevel} of 5`}>
                  {[1, 2, 3, 4, 5].map((n) => <i key={n} className={n <= (track.energyLevel ?? 0) ? "f" : ""} />)}
                </span>
              ) : "—"}
            </dd>
            <dt>Length</dt><dd>{clockTime(track.duration)}</dd>
            {app.library.columns.map((c) => {
              const col = editableFor(c.key, app.library.columns)!;
              return <InlineField key={c.key} track={track} col={col} value={track.custom?.[c.key] ?? null} />;
            })}
          </dl>
        )}
        {tab === "analysis" && (
          <dl className="nv-kv">
            <dt>BPM</dt>
            <dd>{track.bpm != null ? track.bpm.toFixed(1) : "pending"} <span className={`nv-conf ${bpmConf.cls}`}>{track.bpmSource === "curator" ? "set by hand" : bpmConf.word}</span></dd>
            <dt>Key</dt>
            <dd>{key ? `${key} · ${track.musicalKeyName ?? musicalName(key)}` : track.musicalKey === "ambiguous" ? "no clear key" : "pending"} <span className={`nv-conf ${keyConf.cls}`}>{track.keySource === "curator" ? "set by hand" : keyConf.word}</span></dd>
            {track.keyReason && (<><dt>Why</dt><dd className="nv-dim">{track.keyReason}</dd></>)}
            <dt>Danceability</dt><dd>{track.danceability != null ? track.danceability.toFixed(2) : "pending"}</dd>
            <dt>Loudness</dt><dd>{track.lufs != null ? `${track.lufs.toFixed(1)} LUFS · range ${track.lra?.toFixed(1) ?? "—"} LU` : "pending"}</dd>
            <dt>Peak</dt><dd>{track.truePeak != null ? `${track.truePeak.toFixed(1)} dBFS` : "pending"}</dd>
            <dt>Start / mix out</dt><dd>{track.cueIn != null && track.cueOut != null ? `${track.cueIn.toFixed(2)} s – ${clockTime(track.cueOut)}` : "pending"}</dd>
          </dl>
        )}
        {tab === "file" && (
          <dl className="nv-kv">
            <dt>Path</dt><dd className="nv-break">{detail?.path ?? track.path}</dd>
            <dt>Format</dt><dd>{detail?.container ?? (track.path.split(".").pop() ?? "").toUpperCase()}</dd>
            <dt>Artwork</dt>
            <dd>
              {detail ? (detail.hasArtwork ? "Embedded" : "None in the file") : "…"}
              <span className="nv-inline-actions">
                <button className="nv-link" disabled={app.offline} onClick={async () => { if (await changeCover(track)) setDetail(await fetchTrackDetail(track.id)); }}>
                  {detail?.hasArtwork ? "Replace…" : "Add…"}
                </button>
                {detail?.hasArtwork && (
                  <button className="nv-link" disabled={app.offline} onClick={async () => { if (await removeCover(track)) setDetail(await fetchTrackDetail(track.id)); }}>Remove</button>
                )}
              </span>
            </dd>
          </dl>
        )}
      </div>
      <div className="nv-insp-foot">
        {mayDo("library.edit") ? (
          <button className="nv-btn grow" onClick={() => app.navigate("studio", { id: track.id })}>
            <Icon name="edit" /> Edit tags
          </button>
        ) : <span className="grow" />}
        <MoreButton label="Song options" items={() => trackMenu(app, [track])} align="end" />
        {onClose && (
          <button className="nv-iconbtn" title="Close panel" onClick={onClose}>
            <Icon name="panel" />
          </button>
        )}
      </div>
    </aside>
  );
}
